import * as React from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cn } from '../cn'

// Avoid retrying an inaccessible image on every navigation/remount.
const failedAvatarSources = new Map<string, number>()

function Avatar({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      data-slot='avatar'
      className={cn(
        'relative flex size-8 shrink-0 overflow-hidden rounded-full',
        className
      )}
      {...props}
    />
  )
}

function AvatarImage({
  className,
  src,
  onLoadingStatusChange,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  const blocked = !!src && (failedAvatarSources.get(src) ?? 0) > Date.now()
  return (
    <AvatarPrimitive.Image
      data-slot='avatar-image'
      src={blocked ? undefined : src}
      onLoadingStatusChange={(status) => {
        if (src && !blocked) {
          if (status === 'error') {
            if (failedAvatarSources.size >= 256) {
              const oldest = failedAvatarSources.keys().next().value
              if (oldest) failedAvatarSources.delete(oldest)
            }
            failedAvatarSources.set(src, Date.now() + 5 * 60_000)
          } else if (status === 'loaded') failedAvatarSources.delete(src)
        }
        onLoadingStatusChange?.(status)
      }}
      className={cn('aspect-square size-full', className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot='avatar-fallback'
      className={cn(
        'bg-muted flex size-full items-center justify-center rounded-full',
        className
      )}
      {...props}
    />
  )
}

export { Avatar, AvatarImage, AvatarFallback }
